import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, Chrome } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import PasswordInput from '../components/PasswordInput';
import LanguageSelector from '../components/LanguageSelector';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingOAuth, setProcessingOAuth] = useState(false);

  useEffect(() => {
    // If already logged in, redirect to home
    if (user) {
      navigate('/');
      return;
    }
    
    // Check for OAuth callback
    handleOAuthCallback();
  }, [user]);

  const handleOAuthCallback = async () => {
    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      setProcessingOAuth(true);
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      try {
        const response = await axios.post(
          `${API}/auth/google/callback`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        if (response.data.user) {
          // Store token if needed
          if (response.data.token) {
            localStorage.setItem('auth_token', response.data.token);
          }
          
          toast.success(`${t('auth.welcome')}, ${response.data.user.name}!`);
          
          // Clean URL and redirect
          window.history.replaceState(null, '', '/');
          window.location.href = '/';
        }
      } catch (error) {
        console.error('OAuth error:', error);
        toast.error(t('auth.loginError'));
        window.history.replaceState(null, '', '/login');
      } finally {
        setProcessingOAuth(false);
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('auth.loginError'));
      return;
    }

    setLoading(true);
    
    try {
      const loginResult = await authLogin(email, password);
      toast.success(t('auth.loginSuccess'));
      
      // Verificar se é admin e redirecionar para painel admin
      if (loginResult?.user?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || t('auth.loginError');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAppleLogin = async () => {
    try {
      console.log('🍎 Iniciando Apple Sign In...');
      
      // Verificar se AppleID está disponível
      if (typeof window.AppleID === 'undefined') {
        console.error('AppleID SDK não carregado');
        toast.error('Apple Sign In não disponível. Use Google ou Email.');
        return;
      }
      
      const AppleID = window.AppleID;
      console.log('✅ AppleID SDK encontrado');
      
      // Detectar domínio atual
      const currentOrigin = window.location.origin;
      console.log('🌐 Domínio atual:', currentOrigin);
      
      // Configurar
      console.log('⚙️ Configurando AppleID.auth...');
      AppleID.auth.init({
        clientId: 'com.maiRecipes.service',
        scope: 'name email',
        redirectURI: currentOrigin,  // Usar domínio atual
        usePopup: true
      });
      
      // Tentar autenticar
      console.log('🔐 Chamando AppleID.auth.signIn()...');
      const data = await AppleID.auth.signIn();
      
      console.log('📦 Resposta da Apple:', data);
      console.log('📦 Authorization:', data?.authorization);
      console.log('📦 ID Token:', data?.authorization?.id_token);
      
      // Verificar se recebeu token
      const idToken = data?.authorization?.id_token;
      
      if (!idToken) {
        console.error('❌ Token não recebido. Data completa:', JSON.stringify(data));
        throw new Error('Apple não retornou token de autenticação');
      }
      
      console.log('✅ Token recebido, enviando para backend...');
      
      // Enviar para backend
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple`,
        {
          id_token: idToken,
          user: data.user
        },
        { withCredentials: true }
      );
      
      console.log('✅ Backend respondeu:', response.data);
      
      // Atualizar auth context
      if (response.data.user) {
        // Forçar reload para atualizar AuthContext
        localStorage.setItem('apple_login_success', 'true');
        toast.success(`Bem-vindo, ${response.data.user.name}!`);
        
        // Redirect usando navigate (melhor para SPA)
        setTimeout(() => {
          navigate('/');
        }, 500);
      }
    } catch (error) {
      console.error('❌ Apple login error:', error);
      console.error('Error details:', {
        message: error.message,
        error: error.error,
        stack: error.stack
      });
      
      // Mensagem específica
      if (error.error === 'popup_closed_by_user') {
        toast.info('Login cancelado');
      } else if (error.message?.includes('not complete')) {
        toast.error('Autenticação incompleta. Tente novamente ou use Email.');
      } else {
        toast.error('Erro no login com Apple. Use Google ou Email como alternativa.');
      }
    }
  };

  if (processingOAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
      {/* Language Selector - Canto superior direito */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <Card className="w-full max-w-md p-8 shadow-2xl border-2 border-amber-100">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="mAI Recipes" className="w-40 h-40 object-contain" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-amber-900 mb-2">{t('auth.welcomeBack')}</h1>
        <p className="text-center text-gray-600 mb-8">{t('auth.login')}</p>

        {/* Social Login Buttons - DESABILITADOS TEMPORARIAMENTE */}
        {/* 
        <div className="space-y-3 mb-6">
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full py-6 border-2 border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-all"
          >
            <Chrome className="w-5 h-5 mr-2" />
            {t('auth.signInWith')} {t('auth.google')}
          </Button>

          <Button
            onClick={handleAppleLogin}
            variant="outline"
            className="w-full py-6 border-2 border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-all"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {t('auth.signInWith')} {t('auth.apple')}
          </Button>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">{t('auth.orContinueWith')}</span>
          </div>
        </div>
        */}

        {/* Email/Password Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="pl-11"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.password')}
                className="pl-11"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={setRememberMe}
              />
              <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">
                {t('common.rememberMe')}
              </label>
            </div>

            <button
              type="button"
              onClick={() => toast.info(t('auth.forgotPassword'))}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-6"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5 mr-2" />
                {t('auth.login')}
              </>
            )}
          </Button>
        </form>

        {/* Register Link */}
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            {t('auth.dontHaveAccount')}{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              {t('auth.register')}
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
